<template>
  <div class="cart-body-i" :class="{ 'cart-body__items': items.length === 0 }">
    <cart-item
      v-for="item in items"
      :key="item.viewKey || item.key"
      :item="item"
      :shipping="shipping"
    />

    <div v-if="!items.length && !loading" class="emty-cart">
      <div class="emty-cart__center">
        <span class="emty-cart__title" v-html="empty_cart_label"></span>
        <span class="emty-cart__subtitle">{{ sub_empty_cart_label }}</span>
      </div>
    </div>
  </div>
  <div
    v-if="show_upsale && computedUpsaleProducts?.length"
    class="px-30 upsale"
  >
    <div class="upsale__wrapper">
      <h4 class="upsale__title">
        {{
          items.length !== 0 ? upsale_headline : upsale_headline_best_sellers
        }}
      </h4>
      <div :key="items.length" class="flex">
        <component
          :is="items.length !== 0 ? 'upsale-slider' : 'div'"
          :class="{
            'w-full': true,
            paddingX: computedUpsaleProducts.length > 1,
          }"
        >
          <div
            v-for="(i, index) in computedUpsaleProducts"
            class="slick-slide"
            style="display: block"
            :key="index"
          >
            <cart-item
              :item="i"
              :showAddToCartButton="true"
              :shipping="shipping"
            />
          </div>
        </component>
      </div>
    </div>
    <a
      v-if="items.length === 0"
      href="/collections/shop-all"
      class="cart-mini-actions-checkout action-button submit-button"
      >Shop Now</a
    >
  </div>

  <div v-if="items.length !== 0" class="cart-mini-subtotal-wrapper">
    <slot></slot>
    <div class="cart-mini-subtotal">
      <span class="cart-mini-subtotal-heading">Subtotal</span>
      <span
        class="font-normal text-[#727171] line-through"
        v-if="shipping && cartTotalWithShipping"
        >${{ cartTotalWithShipping }}</span
      >
      <span class="cart-mini-subtotal-value money wsgSubtotalPrice">{{
        formatMoney(cartData.total_price)
      }}</span>
    </div>
    <!-- PB Wrap -->
    <div class="progress-bar" v-show="this.freeShipp">
      <p class="progress-bar__text" v-html="freeShipping"></p>

      <!-- Progressbar -->
      <div>
        <div class="progress-bar__wrapper">
          <div class="progress-bar__bar" :style="styleFreeShipping"></div>
        </div>
      </div>
    </div>
    <div class="cart-mini-subtotal-savings">
      <span>Savings</span>
      <span>{{ formatMoney(savingsDiscount) }}</span>
    </div>
    <a
      class="wsgRedirectCart cart-mini-actions-checkout action-button submit-button"
      name="cart"
      href="/checkout"
    >
      <span>{{ "Checkout" }}</span>
    </a>
  </div>
</template>

<script>
import { isGwpLine } from "../../../utils/cart-line";
import { mapActions, mapState } from "vuex";
import { formatMoney } from "@shopify/theme-currency";

export default {
  name: "StoreCart",
  props: {
    shipping: {
      type: String,
      required: true,
    },
    is_first_second_box: {
      type: String,
    },
    timezoneoffset: {
      type: String,
    },
    empty_cart_label: {
      type: String,
      default: "Add your pet’s favorite items to the cart!",
    },
    sub_empty_cart_label: {
      type: String,
      default: "Add your pet’s favorite items to the cart!",
    },
    anouncements: {
      type: Array,
      default: () => [],
    },
    upsale_headline: {
      type: String,
      default: "You might also like",
    },
    upsale_headline_best_sellers: {
      type: String,
      default: "Best Sellers",
    },
    upsale_products: {
      type: String,
      default: "",
    },
    show_upsale: {
      type: String,
      default: "true",
    },
    free_shipping_amt: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      scrolled: false,
      upsaleProducts: JSON.parse(this.upsale_products),
      freeShipp: false,
    };
  },

  computed: {
    ...mapState("cart", [
      "cartData",
      "visible",
      "loading",
      "productsWithDetails",
    ]),
    items() {
      return this.cartData ? this.cartData.items : [];
    },
    computedUpsaleProducts() {
      return this.upsaleProducts.filter((item) => {
        const existsInCart = this.items.some((u) => u.handle === item.handle);

        return !existsInCart && item.available !== false;
      });
    },
    showShipping() {
      return this.items?.some((item) => !item?.selling_plan_allocation);
    },
    headerAnouncements() {
      return window.Shopify?.announcement;
    },
    computedAnouncements() {
      return this.anouncements.length
        ? this.anouncements
        : window.Shopify?.announcement || [];
    },

    cartTotalWithShipping() {
      if (!this.cartData || !this.cartData.items?.length) return 0;

      let hasSellingPlan = false;
      let total = 0;

      this.cartData.items.forEach((item) => {
        const isSellingPlan = !!item.selling_plan_allocation;
        const linePrice = item.final_line_price / 100;

        total += linePrice;

        if (isSellingPlan) {
          hasSellingPlan = true;
        }
      });

      if (hasSellingPlan && this.shipping) {
        total += Number(this.shipping);
      }

      return hasSellingPlan ? total.toFixed(2) : null;
    },
    savingsDiscount() {
      if (!this.items.length) return 0;

      return this.items.reduce((total, item) => {
        // The free gift. Its row shows the price struck through because the
        // discount takes it off, so the whole line is a saving — and it
        // counted for nothing here, since a gift product carries no
        // compare-at price of its own.
        //
        // `original_price` first: once the discount reaches the cart, `price`
        // is what is left of the line after it, and what the buyer saved is
        // what it cost before.
        if (isGwpLine(item)) {
          const giftPrice = Number(
            item.original_price || item.price || item.compare_at_price || 0
          );

          return total + giftPrice * Number(item.quantity || 0);
        }

        const discountedPrice = item.discounted_price || 0;
        const originalPrice =
          this.productsWithDetails[item.id]?.compare_at_price ||
          item?.selling_plan_allocation?.compare_at_price;
        // const compareAt = Number(this.productsWithDetails[item.id]?.compare_at_price) / 100;
        // console.log("compareAt", compareAt);

        if (originalPrice > discountedPrice) {
          const itemSavings = (originalPrice - discountedPrice) * item.quantity;
          return total + itemSavings;
        }

        return total;
      }, 0);
    },
    emptyCart() {
      var status = this.items.length == 0 ? true : false;
      return status;
    },
    freeShipping() {
      if (!this.emptyCart && Number(this.free_shipping_amt) > 0) {
        // this.freeShipp = true; // display block in the cart
        this.showFreeShippingBlock();

        if (
          Number(this.free_shipping_amt) - this.cartData.total_price / 100 >
          0
        ) {
          return (
            'You are <span class="font-bold">$' +
            (
              Number(this.free_shipping_amt) -
              this.cartData.total_price / 100
            ).toFixed(2) +
            "</span> away from free shipping!"
          );
        }
        return "Congrats! You get free shipping.";
      }
      return undefined;
    },
    isFreeShipping() {
      return this.cartData.total_price / 100 > Number(this.free_shipping_amt);
    },
    styleFreeShipping() {
      if (this.isFreeShipping) {
        return "width: 100%;";
      } else {
        return `width: ${
          (this.cartData.total_price / 100 / Number(this.free_shipping_amt)) *
          100
        }%;`;
      }
    },
  },

  watch: {
    visible() {
      document.documentElement.style.overflow = this.visible ? "hidden" : "";
    },
  },
  beforeMount() {
    this.$store.dispatch("cart/initCart");
    window.addEventListener("scroll", this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  mounted() {
    // this.$store.dispatch("cart/initCart");
    // this.handleScroll();
  },

  methods: {
    ...mapActions("cart", {
      init: "initCart",
      hide: "hide",
      show: "show",
    }),

    close() {
      this.hide();
    },

    formatMoney(value) {
      return formatMoney(value, "${{amount}}");
    },

    handleScroll() {
      const scrolled = window.scrollY || window.pageYOffset;
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercent = (scrolled / totalHeight) * 100;

      this.scrolled = scrollPercent >= 1;
    },
    showFreeShippingBlock() {
      this.freeShipp = true;
    },
  },
};
</script>
<style>
.emty-cart {
  width: 100%;
  padding: 15px 0 15px 0;
}
.emty-cart__center {
  text-align: center;
}
.emty-cart__title {
  text-transform: uppercase;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 4px;
  display: block;
  margin-bottom: 5px;
}
.upsale {
  margin-top: auto;
  border-top: 1px solid black;
}
.upsale__wrapper {
  margin-top: 10px;
  margin-bottom: 10px;
}
.upsale__title {
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 10px 0;
}
.cart-mini-content {
  height: 100%;
}
.cart-body {
  display: flex;
  flex-direction: column;
  flex: 1;
}
.cart-body-i {
  height: 100%;
}
.cart-body__items {
  display: flex;
  justify-content: center;
  align-items: center;
}
.loading {
  opacity: 0.5;
  pointer-events: none;
}
.cart-mini-item__add {
  background-color: transparent;
  width: 85px;
  height: 30px;
  border: 1px solid black;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 5px;
}
.gap-none {
  gap: 0 !important;
}
.cart-mini-subtotal-wrapper {
  margin-top: 5px;
}
.cart-mini-item_uspale .cart-mini-item-prices {
  display: flex;
  gap: 5px;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: flex-end;
}

.cart-mini-item_uspale {
  padding: 0;
  border-bottom: none !important;
  margin-bottom: 13px;
}
.slick-initialized .cart-mini-item_uspale {
  margin-bottom: 0px;
}
.upsale .slick-slide {
  height: auto !important;
}
.cart-mini-select-wrapper {
  position: relative;
  margin-top: 10px;
}
.cart-mini-select-wrapper select {
  width: 100%;
  padding: 0 10px;
  height: 30px;
  background-color: transparent;
  border-color: black;
}
.cart-mini-select-wrapper .cart-shipping-calculator-select-icon {
  right: 10px;
}
.cart-mini-item-add-subscribe {
  background-color: transparent;
  border: none;
}
.cart-mini-item-column-image {
  position: relative;
}
.badges-wrapper__cart {
  top: -3px;
  left: 3px;
}
.badges-wrapper__cart .badge {
  font-size: 8px;
  padding: 1px 2px;
}
</style>
